<template>
    <a
        v-if="cta === 'fix-card'"
        :href="route('payment-methods.index')"
        :class="buttonClass"
        data-test="pro-upgrade-button"
    >
        Update card
    </a>
    <button
        v-else-if="cta !== null"
        type="button"
        :disabled="processing"
        :class="buttonClass"
        data-test="pro-upgrade-button"
        @click="showUpsell = true"
    >
        {{ processing ? 'Processing...' : 'Upgrade to Pro' }}
    </button>

    <RiskGuardProUpsellModal
        v-if="canBuy"
        :show="showUpsell"
        :processing="processing"
        :stripe-data="cardStripeData"
        :user-payment-method="userPaymentMethod"
        :subscription="subscriptionProtection"
        :show-features="true"
        :notice="tierChangedNotice"
        @dismiss="closeUpsell"
        @subscribe="onSubscribeStart"
    />

    <SubscriptionProtection
        v-if="canBuy"
        ref="subscriptionProtection"
        subscription-type="protect"
        plan-slug="protect-pro-tiered"
        billing-cycle="annual"
        :terms-accepted="termsAccepted"
        :has-payment-method="hasPaymentMethod"
        :user-payment-method="userPaymentMethod"
        :stripe-data="stripeData ?? []"
        @success="handleSubscribeSuccess"
        @error="handleSubscribeError"
        @cancel="processing = false"
        @tier-changed="handleTierChanged"
    />
</template>

<script setup>
// "Upgrade to Pro" for standalone pages that have no subscribe flow of their own (lease
// information, create maintenance) — 86bcd9hg3. Opens the account-wide Pro pop-up and buys
// right here, like the property page does, instead of sending the landlord to /pricing.
// 86bc9e403: the pop-up is the combined in-place checkout — it collects the count and card
// and subscribes itself via the SubscriptionProtection ref it is handed.
// Follows the shared rule: hidden once on Pro, "Update card" after a failed payment, and a
// landlord on an earlier plan gets "Switch to new plan" inside the pop-up.
import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import RiskGuardProUpsellModal from '@/Components/RiskGuardProUpsellModal.vue'
import SubscriptionProtection from '@/Shared/V2/SubscriptionProtection.vue'
import { proCta } from '@/Shared/proPlanState'

const props = defineProps({
    // The page's Stripe data (FunctionHelpers::stripeDataForAddingCard when there is no card).
    stripeData: {
        type: [Object, Array],
        default: null,
    },
    hasPaymentMethod: {
        type: Boolean,
        default: false,
    },
    userPaymentMethod: {
        type: Object,
        default: null,
    },
    buttonClass: {
        type: String,
        default: 'mt-4 inline-flex items-center gap-2 rounded-md bg-gold-500 px-4 py-2 text-sm font-bold text-[#7d6310] hover:bg-gold-700 disabled:opacity-50',
    },
})

const page = usePage()
const showUpsell = ref(false)
// Why the pop-up reopened (the price moved before the charge); shown inside it.
const tierChangedNotice = ref(null)
const closeUpsell = () => {
    showUpsell.value = false
    tierChangedNotice.value = null
}

// 86bc9e403: the combined checkout stays open through the subscribe, so close it on success.
const handleSubscribeSuccess = () => {
    processing.value = false
    closeUpsell()
}
const processing = ref(false)
const subscriptionProtection = ref(null)

const cta = computed(() => proCta(page.props.auth?.user?.pro_plan))
// Basic buys here; an earlier plan switches inside the pop-up. Pro / failed payment: no pop-up.
const canBuy = computed(() => cta.value === 'upgrade' || cta.value === 'switch')
const termsAccepted = computed(() => Boolean(page.props.user?.protect_tos_accepted_at))

// The combined checkout can only collect a new card when a SetupIntent is here; a landlord who
// already has a card shows it instead, so no SetupIntent is needed.
const cardStripeData = computed(() => (props.stripeData?.publishable_key && props.stripeData?.setup_intent ? props.stripeData : null))

// The pop-up now subscribes itself; we only flip the processing flag so the trigger button
// reflects the in-flight purchase. Success / error / cancel clear it via SubscriptionProtection.
const onSubscribeStart = () => {
    processing.value = true
}

// 86bc9e403 #1: a declined card / failed subscribe clears processing (so the pop-up's Subscribe
// button re-enables) and shows the reason inside the still-open pop-up via its notice.
const handleSubscribeError = ({ error } = {}) => {
    processing.value = false
    tierChangedNotice.value = error || 'We could not start your subscription. Please try again.'
}

// The price moved since the pop-up (a property was added meanwhile); nothing was charged.
// Refresh the quote and show the pop-up again with the new price.
const handleTierChanged = ({ message }) => {
    processing.value = false
    router.reload({
        only: ['auth'],
        preserveScroll: true,
        onSuccess: () => {
            tierChangedNotice.value = message || null
            showUpsell.value = true
        },
    })
}
</script>
